import { Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'

/** OBB estricto con tamaño real del ítem (160 y RELAX usan el mismo criterio). */
export function buildItemStrictOBB(
  matrix: Matrix4,
  item: { width?: number; height?: number; thickness?: number },
  target?: OBB,
): OBB {
  const width = Number(item.width || 0)
  const height = Number(item.height || 0)
  const thickness = Number(item.thickness || 0)
  const obb = target ?? new OBB()
  const pos = new Vector3()
  const quat = new Quaternion()
  matrix.decompose(pos, quat, new Vector3())
  const center = new Vector3(width / 2, height / 2, thickness / 2)
  center.applyQuaternion(quat).add(pos)
  const rot = new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(quat))
  obb.center.copy(center)
  obb.halfSize.set(width / 2, height / 2, thickness / 2)
  obb.rotation.copy(rot)
  return obb
}

/**
 * Imán en planta (origen↔origen), independiente de la rotación actual.
 * Al acercar un módulo a la cara lateral del 166 tras haber estado en pata (yaw 90°),
 * el OBB orientado del chaise (fondo ~1600) no llega a intersectar; el 160 (~920) sí.
 * Mismo alcance para 160 y RELAX: half-diagonal plan × scale.
 */
export function isWithinPlanMagnetRange(
  aPos: { x: number; z: number },
  aWidth: number,
  aThickness: number,
  bPos: { x: number; z: number },
  bWidth: number,
  bThickness: number,
  scale = 1.5,
): boolean {
  const reach =
    (Math.hypot(Math.max(0, aWidth), Math.max(0, aThickness)) * 0.5 +
      Math.hypot(Math.max(0, bWidth), Math.max(0, bThickness)) * 0.5) *
    scale
  return Math.hypot(aPos.x - bPos.x, aPos.z - bPos.z) <= reach
}

/** ¿El módulo penetra otra linkedItem de asiento? (mismo test para 160 y RELAX). */
export function straightModulePenetratesOthers(
  matrix: Matrix4,
  item: { id?: string; catalogID?: unknown; width?: number; height?: number; thickness?: number; type?: string },
  items: Map<string, any>,
  excludeIds: Iterable<string> = [],
): string | null {
  const exclude = new Set(Array.from(excludeIds, String))
  if (item.id) exclude.add(String(item.id))
  const selfOBB = buildItemStrictOBB(matrix, item)

  for (const [otherId, other] of items) {
    if (exclude.has(String(otherId))) continue
    if (!other || other.type !== 'linkedItem' || !other.matrix) continue
    if (other.visible === false) continue
    if (/^BR\d+/i.test(String(other.catalogID || ''))) continue
    const otherOBB = buildItemStrictOBB(other.matrix, other)
    if (selfOBB.intersectsOBB(otherOBB)) return String(otherId)
  }
  return null
}
